import React, { useState, useEffect, useRef } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import { useSearchParams, useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import toast from 'react-hot-toast'
import ChatButton from '../../components/ChatButton'
import { trackPolicyDetailsView, trackPolicyDownload, trackClaimSubmit, openChatWithContext } from '../../utils/eventTracking'
import { 
  ArrowLeft,
  CreditCard, 
  Calendar,
  DollarSign,
  Shield,
  FileText,
  Download,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Heart,
  Building,
  Car,
  Eye,
  Brain,
  Smile,
  Stethoscope,
  Star,
  TrendingUp,
  Award,
  AlertCircle,
  MessageCircle,
  MessageSquare
} from 'lucide-react'
import { 
  enhancePolicyData, 
  formatCurrency, 
  formatPercentage,
  getStatusColor,
  getStatusBadgeColor,
  calculateNoClaimBonus,
  calculateDynamicPremium,
  getDynamicCoveragePercentage,
  getDynamicDeductibleAmount,
  getDynamicCoPaymentPercentage,
  getDynamicRoomRentLimit,
  getDynamicOutOfNetworkPercentage,
  calculateClaimsStatistics,
  getDynamicWaitingPeriods,
  hasClaims,
  isEligibleForNoClaimBonus,
  getPolicyAge,
  isNearRenewal,
  getPolicyRiskLevel
} from '../../utils/policyCalculations'

const PolicyDetails = () => {
  const { getUserPolicies, getUserPoliciesWithClaims, getClaims } = useApi()
  const { userId } = useUserId()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const [policy, setPolicy] = useState(null)
  const [enhancedPolicy, setEnhancedPolicy] = useState(null)
  const [claims, setClaims] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const pdfContentRef = useRef(null)
  const modalContentRef = useRef(null)
  
  const policyId = searchParams.get('policy_id')

  useEffect(() => {
    if (userId && policyId) {
      loadPolicyDetails()
    }
  }, [userId, policyId])

  const loadPolicyDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get all user policies and find the specific one
      const policies = await getUserPolicies(userId)
      const selectedPolicy = policies.find(p => p.policy_id === policyId)
      
      if (selectedPolicy) {
        setPolicy(selectedPolicy)
        // Enhance policy data with dynamic calculations
        const enhanced = enhancePolicyData(selectedPolicy)
        setEnhancedPolicy(enhanced)
        
        // Track policy details view
        trackPolicyDetailsView(userId, policyId, {
          policy_type: selectedPolicy.product_category,
          status: selectedPolicy.policy_status,
          coverage_amount: selectedPolicy.sum_insured
        })
        
        // Fetch claims for this specific policy
        try {
          const claimsData = await getClaims({ policy_id: policyId })
          setClaims(claimsData || [])
        } catch (claimsError) {
          console.error('Error fetching claims:', claimsError)
          setClaims([])
        }
      } else {
        setError('Policy not found')
      }
    } catch (error) {
      setError('Failed to load policy details')
    } finally {
      setLoading(false)
    }
  }

  // Get claim status icon
  const getClaimStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return <CheckCircle className="h-3 w-3" />
      case 'APPROVED':
        return <CheckCircle className="h-3 w-3" />
      case 'PENDING':
      case 'UNDER_REVIEW':
        return <Clock className="h-3 w-3" />
      case 'REJECTED':
        return <XCircle className="h-3 w-3" />
      case 'DRAFT':
      case 'SUBMITTED':
        return <FileText className="h-3 w-3" />
      default:
        return <AlertCircle className="h-3 w-3" />
    }
  }

  const handleBack = () => {
    navigate(`/customer?user_id=${userId}`)
  }

  const handleFileClaim = () => {
    navigate(`/customer/claims?user_id=${userId}&policy_id=${policyId}`)
  }

  const handleDownloadPolicy = () => {
    setShowPdfModal(true)
  }

  const handleGeneratePdf = async () => {
    try {
      if (!modalContentRef.current) {
        console.error('Modal content ref not found')
        toast.error('Error: Content not found. Please try again.')
        return
      }

      // Temporarily remove height constraints for full content capture
      const modalContent = modalContentRef.current
      const originalMaxHeight = modalContent.style.maxHeight
      const originalOverflow = modalContent.style.overflow
      
      // Set to auto height for full capture
      modalContent.style.maxHeight = 'none'
      modalContent.style.overflow = 'visible'

      // Show loading state immediately
      const generateButton = document.querySelector('[data-generate-pdf]')
      const generateButtonText = generateButton?.querySelector('span')
      console.log('Generate button found:', generateButton)
      if (generateButton) {
        generateButton.disabled = true
        generateButton.style.opacity = '0.7'
        if (generateButtonText) {
          generateButtonText.textContent = 'Generating...'
        }
      }

      // Create canvas from the modal content
      let canvas
      try {
        canvas = await html2canvas(modalContentRef.current, {
          scale: 1.5, // Reduced scale for better performance
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: true, // Enable logging for debugging
          width: modalContentRef.current.scrollWidth,
          height: modalContentRef.current.scrollHeight,
          removeContainer: false, // Keep container to ensure full content
          scrollX: 0,
          scrollY: 0,
          windowWidth: modalContentRef.current.scrollWidth,
          windowHeight: modalContentRef.current.scrollHeight
        })
      } catch (canvasError) {
        console.error('Canvas creation failed:', canvasError)
        throw new Error('Failed to create PDF content. Please try again.')
      }

      // Create PDF - simple approach, print page as it is
      const imgData = canvas.toDataURL('image/png', 1.0)
      
      let pdf
      try {
        pdf = new jsPDF('p', 'mm', 'a4')
      } catch (pdfError) {
        console.error('PDF creation failed:', pdfError)
        throw new Error('Failed to initialize PDF. Please try again.')
      }
      
      // Calculate dimensions
      const imgWidth = 190
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      
      // Add the page content as it is
      try {
        if (imgHeight <= 280) {
          // Single page
          pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight)
        } else {
          // Multiple pages
          let yOffset = 10
          let remainingHeight = imgHeight
          let pageNumber = 1
          
          while (remainingHeight > 0) {
            if (pageNumber > 1) {
              pdf.addPage()
            }
            
            const contentHeight = Math.min(remainingHeight, 280)
            pdf.addImage(imgData, 'PNG', 10, yOffset, imgWidth, contentHeight)
            
            remainingHeight -= 280
            pageNumber++
          }
        }
      } catch (imageError) {
        console.error('Image addition failed:', imageError)
        throw new Error('Failed to add content to PDF. Please try again.')
      }

      // Download the PDF
      const fileName = `Policy_${policy.policy_number || policyId}_${new Date().toISOString().split('T')[0]}.pdf`
      pdf.save(fileName)

      // Restore original modal styles
      modalContent.style.maxHeight = originalMaxHeight
      modalContent.style.overflow = originalOverflow

      // Close modal and reset button
      setShowPdfModal(false)
      
      // Reset generate button state
      const resetButton = document.querySelector('[data-generate-pdf]')
      const resetButtonText = resetButton?.querySelector('span')
      if (resetButton) {
        resetButton.disabled = false
        resetButton.style.opacity = '1'
        if (resetButtonText) {
          resetButtonText.textContent = 'Generate PDF'
        }
      }

    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Error generating PDF. Please try again.')
      
      // Restore original modal styles
      if (modalContentRef.current) {
        modalContentRef.current.style.maxHeight = originalMaxHeight
        modalContentRef.current.style.overflow = originalOverflow
      }
      
      // Reset generate button state
      const errorButton = document.querySelector('[data-generate-pdf]')
      const errorButtonText = errorButton?.querySelector('span')
      if (errorButton) {
        errorButton.disabled = false
        errorButton.style.opacity = '1'
        if (errorButtonText) {
          errorButtonText.textContent = 'Generate PDF'
        }
      }
    }
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Policy</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button 
          onClick={handleBack}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    )
  }

  if (!policy) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Policy Not Found</h3>
        <p className="text-gray-500 mb-4">The requested policy could not be found.</p>
        <button 
          onClick={handleBack}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6" ref={pdfContentRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Policy Details</h1>
            <p className="text-xs sm:text-sm text-gray-600">Policy #{policy.policy_number || 'N/A'}</p>
          </div>
        </div>
        <button
          onClick={() => {
            const policyNumber = policy.policy_number || policyId
            const policyType = policy.product_category || 'N/A'
            const coverageAmount = policy.sum_insured ? `₹${policy.sum_insured.toLocaleString('en-IN')}` : 'N/A'
            const policyStatus = policy.policy_status || 'N/A'
            
            openChatWithContext(
              `I need help with my policy. Policy Number: ${policyNumber}, Type: ${policyType}, Coverage: ${coverageAmount}, Status: ${policyStatus}. I have a question about this policy.`,
              'policy_details_page'
            )
          }}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm sm:text-base"
        >
          <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="font-medium hidden sm:inline">Chat about Policy</span>
          <span className="font-medium sm:hidden">Chat</span>
        </button>
      </div>

      {/* Status Badge */}
      <div className="flex justify-start">
        <span className={`px-4 py-2 text-sm font-bold rounded-full flex items-center space-x-2 ${getStatusColor(policy.policy_status)}`}>
          <CheckCircle className="h-4 w-4" />
          <span>{policy.policy_status}</span>
        </span>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Policy Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Policy Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-gray-600">Policy Number</label>
                <p className="text-sm text-gray-900">{policy.policy_number || 'Not available'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-600">Policy Name</label>
                <p className="text-sm text-gray-900">{policy.policy_name || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-600">Product Name</label>
                <p className="text-sm text-gray-900">{policy.product_name || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-600">Product Category</label>
                <p className="text-sm text-gray-900">{policy.product_category || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-600">Policy Status</label>
                <p className="text-sm text-gray-900">{policy.policy_status || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-600">Policy Term</label>
                <p className="text-sm text-gray-900">{policy.policy_term_years || 'Not specified'} years</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-bold text-gray-600">Policy Description</label>
                <p className="text-sm text-gray-900">{policy.policy_description || 'Not specified'}</p>
              </div>
        </div>
      </div>

          {/* Financial Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Financial Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Sum Insured</label>
                <p className="text-2xl font-bold text-gray-900">
                  {enhancedPolicy?.formattedSumInsured || formatCurrency(policy.sum_insured)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Annual Premium</label>
                <p className="text-xl font-semibold text-blue-600">
                  {enhancedPolicy?.formattedAnnualPremium || formatCurrency(policy.annual_premium)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Premium Amount</label>
                <p className="text-xl font-semibold text-green-600">
                  {enhancedPolicy?.formattedPremium || formatCurrency(policy.premium_amount)}
                </p>
            </div>
          </div>
          </div>

          {/* Detailed Financial Breakdown */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Detailed Financial Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Tax Amount</label>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(policy.tax_amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Total Premium</label>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(policy.total_premium_amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Premium Frequency</label>
                <p className="text-lg font-semibold text-gray-900">{policy.premium_frequency || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Coverage Percentage</label>
                <p className="text-lg font-semibold text-blue-600">{formatPercentage(enhancedPolicy?.dynamicCoveragePercentage || policy.coverage_percentage)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Deductible Amount</label>
                <p className="text-lg font-semibold text-gray-900">{enhancedPolicy?.formattedDeductible || formatCurrency(enhancedPolicy?.dynamicDeductibleAmount || policy.deductible_amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Co-payment %</label>
                <p className="text-lg font-semibold text-gray-900">{formatPercentage(enhancedPolicy?.dynamicCoPaymentPercentage || policy.co_payment_percentage)}</p>
              </div>
            </div>
          </div>

          {/* Policy Period & Terms */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Policy Period & Terms
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Start Date</label>
                <p className="text-gray-900">
                  {policy.policy_start_date ? new Date(policy.policy_start_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">End Date</label>
                <p className="text-gray-900">
                  {policy.policy_end_date ? new Date(policy.policy_end_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Term</label>
                <p className="text-gray-900">{policy.policy_term_years || 'N/A'} years</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Grace Period</label>
                <p className="text-gray-900">{policy.grace_period_days || 'N/A'} days</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Renewal</label>
                <p className="text-gray-900">
                  {policy.last_renewal_date ? new Date(policy.last_renewal_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Next Renewal</label>
                <p className="text-gray-900">
                  {policy.next_renewal_date ? new Date(policy.next_renewal_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Coverage & Benefits - Health Insurance */}
          {policy.product_category === 'HEALTH' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Coverage & Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Inpatient Coverage</label>
                  <p className={`text-sm font-semibold ${policy.inpatient_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.inpatient_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Outpatient Coverage</label>
                  <p className={`text-sm font-semibold ${policy.outpatient_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.outpatient_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Day Care Coverage</label>
                  <p className={`text-sm font-semibold ${policy.day_care_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.day_care_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Emergency Coverage</label>
                  <p className={`text-sm font-semibold ${policy.emergency_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.emergency_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Ambulance Coverage</label>
                  <p className={`text-sm font-semibold ${policy.ambulance_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.ambulance_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Network Hospitals</label>
                  <p className={`text-sm font-semibold ${policy.network_hospitals_covered ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.network_hospitals_covered ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Out of Network</label>
                  <p className={`text-sm font-semibold ${policy.out_of_network_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.out_of_network_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Out of Network %</label>
                  <p className="text-sm font-semibold text-gray-900">{formatPercentage(enhancedPolicy?.dynamicOutOfNetworkPercentage || policy.out_of_network_coverage_percentage)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Room Rent Limit/Day</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {enhancedPolicy?.formattedRoomRent || formatCurrency(enhancedPolicy?.dynamicRoomRentLimit || policy.room_rent_limit_per_day)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Life Insurance Benefits */}
          {policy.product_category === 'LIFE' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Life Insurance Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Death Benefit</label>
                  <p className="text-sm font-semibold text-green-600">Covered</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Maturity Benefit</label>
                  <p className="text-sm font-semibold text-green-600">
                    {policy.product_subcategory === 'ENDOWMENT' || policy.product_subcategory === 'WHOLE_LIFE' ? 'Covered' : 'Not Applicable'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Accidental Death Benefit</label>
                  <p className="text-sm font-semibold text-green-600">Covered</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Policy Term</label>
                  <p className="text-sm font-semibold text-gray-900">{policy.policy_term_years || 'N/A'} years</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Maturity Date</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {policy.policy_maturity_date ? new Date(policy.policy_maturity_date).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Maturity Value</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {policy.maturity_value ? formatCurrency(policy.maturity_value) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Bonus Amount</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {policy.bonus_amount ? formatCurrency(policy.bonus_amount) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Surrender Value</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {policy.surrender_value ? formatCurrency(policy.surrender_value) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Specialized Coverage - Health Insurance Only */}
          {policy.product_category === 'HEALTH' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Heart className="h-5 w-5 mr-2" />
                Specialized Coverage
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Dental Coverage</label>
                  <p className={`text-sm font-semibold ${policy.dental_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.dental_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Vision Coverage</label>
                  <p className={`text-sm font-semibold ${policy.vision_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.vision_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Mental Health Coverage</label>
                  <p className={`text-sm font-semibold ${policy.mental_health_coverage ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.mental_health_coverage ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Preventive Health Checkup</label>
                  <p className={`text-sm font-semibold ${policy.preventive_health_checkup ? 'text-green-600' : 'text-red-600'}`}>
                    {policy.preventive_health_checkup ? 'Yes' : 'No'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Wellness Benefits</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {policy.wellness_benefits ? policy.wellness_benefits.join(', ') : 'None'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Preferred Hospitals</label>
                  <p className="text-sm font-semibold text-gray-900">
                    {policy.preferred_hospitals || 'None'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Waiting Periods - Health Insurance Only */}
          {policy.product_category === 'HEALTH' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Waiting Periods
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Pre-existing Conditions</label>
                  <p className="text-sm font-semibold text-gray-900">{enhancedPolicy?.waitingPeriods?.preExisting || policy.pre_existing_condition_waiting_months || 'N/A'} months</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Maternity</label>
                  <p className="text-sm font-semibold text-gray-900">{enhancedPolicy?.waitingPeriods?.maternity || policy.maternity_waiting_months || 'N/A'} months</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Critical Illness</label>
                  <p className="text-sm font-semibold text-gray-900">{enhancedPolicy?.waitingPeriods?.criticalIllness || policy.critical_illness_waiting_months || 'N/A'} months</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Bariatric Surgery</label>
                  <p className="text-sm font-semibold text-gray-900">{enhancedPolicy?.waitingPeriods?.bariatric || policy.bariatric_surgery_waiting_months || 'N/A'} months</p>
                </div>
              </div>
            </div>
          )}

          {/* Claims Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Claims Information
            </h3>
            
            {/* Claims Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Total Claims Filed</label>
                <p className="text-sm font-bold text-gray-900">{claims.length}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Approved Claims</label>
                <p className="text-sm font-bold text-green-600">{claims.filter(c => c.claim_status === 'APPROVED' || c.claim_status === 'PAID').length}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Rejected Claims</label>
                <p className="text-sm font-bold text-red-600">{claims.filter(c => c.claim_status === 'REJECTED').length}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Pending Claims</label>
                <p className="text-sm font-bold text-yellow-600">{claims.filter(c => c.claim_status === 'PENDING' || c.claim_status === 'UNDER_REVIEW' || c.claim_status === 'DRAFT' || c.claim_status === 'SUBMITTED').length}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Total Amount Paid</label>
                <p className="text-sm font-bold text-green-600">{formatCurrency(claims.reduce((sum, c) => sum + (parseFloat(c.paid_amount) || 0), 0))}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Total Amount Claimed</label>
                <p className="text-sm font-bold text-blue-600">{formatCurrency(claims.reduce((sum, c) => sum + (parseFloat(c.total_bill_amount) || 0), 0))}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">No Claim Bonus Applicable</label>
                <p className={`text-sm font-semibold ${isEligibleForNoClaimBonus(policy) ? 'text-green-600' : 'text-red-600'}`}>
                  {isEligibleForNoClaimBonus(policy) ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">No Claim Bonus %</label>
                <p className="text-sm font-semibold text-gray-900">{formatPercentage(enhancedPolicy?.dynamicNoClaimBonus || calculateNoClaimBonus(policy))}</p>
              </div>
            </div>

            {/* Claims List */}
            {claims.length > 0 ? (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Recent Claims</h4>
                <div className="space-y-3">
                  {claims.slice(0, 5).map((claim, index) => (
                    <div 
                      key={claim.claim_id || index} 
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/customer/claims/${claim.claim_id}?user_id=${userId}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm font-medium text-gray-900">Claim #{claim.claim_number || claim.claim_id}</span>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center space-x-1 ${getStatusBadgeColor(claim.claim_status)}`}>
                              {getClaimStatusIcon(claim.claim_status)}
                              <span>{claim.claim_status}</span>
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Amount:</span>
                              <span className="ml-2 font-semibold">{formatCurrency(claim.approved_amount || claim.total_bill_amount || 0)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <span className="ml-2">{new Date(claim.created_at).toLocaleDateString()}</span>
                            </div>
                            {claim.hospital_name && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Hospital:</span>
                                <span className="ml-2">{claim.hospital_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/customer/claims/${claim.claim_id}?user_id=${userId}`)
                          }}
                          className="ml-4 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                  {claims.length > 5 && (
                    <div className="text-center pt-2">
                      <button
                        onClick={() => navigate(`/customer/claims?user_id=${userId}&policy_id=${policyId}`)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View All {claims.length} Claims
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500">No claims available for this policy</p>
              </div>
            )}
          </div>

          {/* Policy Insights & Analytics */}
          {enhancedPolicy && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Policy Insights & Analytics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Policy Age</label>
                  <p className="text-sm font-semibold text-gray-900">{getPolicyAge(policy)} years</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Risk Level</label>
                  <p className={`text-sm font-semibold ${getPolicyRiskLevel(policy) === 'LOW' ? 'text-green-600' : getPolicyRiskLevel(policy) === 'MEDIUM' ? 'text-yellow-600' : 'text-red-600'}`}>
                    {getPolicyRiskLevel(policy)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Claims History</label>
                  <p className="text-sm font-semibold text-gray-900">{hasClaims(policy) ? 'Has Claims' : 'No Claims'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Near Renewal</label>
                  <p className={`text-sm font-semibold ${isNearRenewal(policy) ? 'text-yellow-600' : 'text-green-600'}`}>
                    {isNearRenewal(policy) ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Life Insurance Beneficiaries & Nominees */}
          {policy.product_category === 'LIFE' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Beneficiaries & Nominees
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Primary Beneficiary</label>
                  <p className="text-sm font-semibold text-gray-900">{policy.beneficiary_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Beneficiary Relationship</label>
                  <p className="text-sm font-semibold text-gray-900">{policy.beneficiary_relationship || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Beneficiary Contact</label>
                  <p className="text-sm font-semibold text-gray-900">{policy.beneficiary_contact || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Nominee Name</label>
                  <p className="text-sm font-semibold text-gray-900">{policy.nominee_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Nominee Relationship</label>
                  <p className="text-sm font-semibold text-gray-900">{policy.nominee_relationship || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Policy Status & Underwriting */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              Policy Status & Underwriting
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Status</label>
                <p className={`text-sm font-bold ${enhancedPolicy?.statusColor || getStatusColor(policy.policy_status)}`}>
                  {policy.policy_status || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Underwriting Status</label>
                <p className={`text-sm font-semibold ${enhancedPolicy?.underwritingStatusColor || getStatusColor(policy.underwriting_status)}`}>
                  {policy.underwriting_status || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Renewal Status</label>
                <p className={`text-sm font-semibold ${enhancedPolicy?.renewalStatusColor || getStatusColor(policy.renewal_status)}`}>
                  {policy.renewal_status || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Distribution Channel</label>
                <p className="text-sm font-semibold text-gray-900">{policy.distribution_channel || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Commission Structure</label>
                <p className="text-sm font-semibold text-gray-900">{policy.commission_structure || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Agent ID</label>
                <p className="text-sm font-semibold text-gray-900">{policy.agent_id || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Branch ID</label>
                <p className="text-sm font-semibold text-gray-900">{policy.branch_id || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Policy Actions */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-3 shadow-sm" data-pdf-hide="true">
            <h3 className="text-sm font-bold text-gray-900 mb-2">Policy Actions</h3>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={handleDownloadPolicy}
                data-download-button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded flex items-center justify-center space-x-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex-1 min-w-0"
                style={{ color: 'white' }}
              >
                <Download className="h-4 w-4" style={{ color: 'white' }} />
                <span className="text-sm" style={{ color: 'white' }}>Download</span>
              </button>
              <button 
                onClick={handleFileClaim}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded flex items-center justify-center space-x-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex-1 min-w-0"
              >
                <Plus className="h-4 w-4" />
                <span className="text-sm">File Claim</span>
              </button>
              <button 
                onClick={handleBack}
                className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded flex items-center justify-center space-x-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex-1 min-w-0"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Policy Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Policy Summary
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Number</label>
                <p className="text-gray-900 font-semibold">{policy.policy_number || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Name</label>
                <p className="text-gray-900">{policy.policy_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Sum Insured</label>
                <p className="text-gray-900">{enhancedPolicy?.formattedSumInsured || formatCurrency(policy.sum_insured)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <span className={`px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(policy.policy_status)}`}>
                  {policy.policy_status || 'N/A'}
            </span>
          </div>
        </div>
      </div>

          {/* Quick Actions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={handleDownloadPolicy}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm flex-1 min-w-0"
              >
                Download Policy
              </button>
              <button 
                onClick={handleFileClaim}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm flex-1 min-w-0"
              >
                File New Claim
              </button>
              <button 
                onClick={handleBack}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm flex-1 min-w-0"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>











      {/* PDF Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Policy Document Preview</h2>
                <button
                  onClick={() => setShowPdfModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]" ref={modalContentRef}>
              <div className="bg-white">
                <h1 className="text-3xl font-bold text-center mb-8 text-blue-600">Policy Document</h1>
                
                {/* Policy Details Table */}
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Policy Information</h2>
                  <table className="w-full border-collapse border border-gray-300">
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Policy Number</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_number || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Policy Name</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Product Name</td>
                        <td className="border border-gray-300 p-3">{policy?.product_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Policy Description</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_description || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Status</td>
                        <td className="border border-gray-300 p-3">
                          <span className={`px-2 py-1 rounded-full text-sm ${
                            policy?.policy_status === 'ACTIVE' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {policy?.policy_status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Policy Holder Information */}
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Policy Holder Information</h2>
                  <table className="w-full border-collapse border border-gray-300">
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Policy Holder Name</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_holder_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Relationship</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_holder_relationship || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Financial Information */}
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Financial Information</h2>
                  <table className="w-full border-collapse border border-gray-300">
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Sum Insured</td>
                        <td className="border border-gray-300 p-3">{enhancedPolicy?.formattedSumInsured || formatCurrency(policy?.sum_insured) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Annual Premium</td>
                        <td className="border border-gray-300 p-3">{enhancedPolicy?.formattedAnnualPremium || formatCurrency(policy?.annual_premium) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Premium Amount</td>
                        <td className="border border-gray-300 p-3">{enhancedPolicy?.formattedPremium || formatCurrency(policy?.premium_amount) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Tax Amount</td>
                        <td className="border border-gray-300 p-3">{formatCurrency(policy?.tax_amount) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Total Premium</td>
                        <td className="border border-gray-300 p-3">{formatCurrency(policy?.total_premium_amount) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Premium Frequency</td>
                        <td className="border border-gray-300 p-3">{policy?.premium_frequency || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Coverage Percentage</td>
                        <td className="border border-gray-300 p-3">{formatPercentage(enhancedPolicy?.dynamicCoveragePercentage || policy?.coverage_percentage) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Deductible Amount</td>
                        <td className="border border-gray-300 p-3">{enhancedPolicy?.formattedDeductible || formatCurrency(enhancedPolicy?.dynamicDeductibleAmount || policy?.deductible_amount) || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Co-payment %</td>
                        <td className="border border-gray-300 p-3">{formatPercentage(enhancedPolicy?.dynamicCoPaymentPercentage || policy?.co_payment_percentage) || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Policy Period & Terms */}
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Policy Period & Terms</h2>
                  <table className="w-full border-collapse border border-gray-300">
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Start Date</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_start_date ? new Date(policy.policy_start_date).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">End Date</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_end_date ? new Date(policy.policy_end_date).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Policy Term</td>
                        <td className="border border-gray-300 p-3">{policy?.policy_term_years ? `${policy.policy_term_years} years` : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Grace Period</td>
                        <td className="border border-gray-300 p-3">{policy?.grace_period_days ? `${policy.grace_period_days} days` : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Last Renewal</td>
                        <td className="border border-gray-300 p-3">{policy?.last_renewal_date ? new Date(policy.last_renewal_date).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Next Renewal</td>
                        <td className="border border-gray-300 p-3">{policy?.next_renewal_date ? new Date(policy.next_renewal_date).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Coverage & Benefits - Health Insurance */}
                {policy?.product_category === 'HEALTH' && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Coverage & Benefits</h2>
                    <table className="w-full border-collapse border border-gray-300">
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Inpatient Coverage</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.inpatient_coverage ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.inpatient_coverage ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Outpatient Coverage</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.outpatient_coverage ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.outpatient_coverage ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Day Care Coverage</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.day_care_coverage ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.day_care_coverage ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Emergency Coverage</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.emergency_coverage ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.emergency_coverage ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Ambulance Coverage</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.ambulance_coverage ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.ambulance_coverage ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Network Hospitals</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.network_hospitals_covered ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.network_hospitals_covered ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Out of Network</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`font-semibold ${policy?.out_of_network_coverage ? 'text-green-600' : 'text-red-600'}`}>
                              {policy?.out_of_network_coverage ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Out of Network %</td>
                          <td className="border border-gray-300 p-3">{formatPercentage(enhancedPolicy?.dynamicOutOfNetworkPercentage || policy?.out_of_network_coverage_percentage) || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Room Rent Limit</td>
                          <td className="border border-gray-300 p-3">{enhancedPolicy?.formattedRoomRent || formatCurrency(enhancedPolicy?.dynamicRoomRentLimit || policy?.room_rent_limit_per_day) || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Life Insurance Benefits */}
                {policy?.product_category === 'LIFE' && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Life Insurance Benefits</h2>
                    <table className="w-full border-collapse border border-gray-300">
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Death Benefit</td>
                          <td className="border border-gray-300 p-3">
                            <span className="font-semibold text-green-600">Covered</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Maturity Benefit</td>
                          <td className="border border-gray-300 p-3">
                            <span className="font-semibold text-green-600">
                              {policy?.product_subcategory === 'ENDOWMENT' || policy?.product_subcategory === 'WHOLE_LIFE' ? 'Covered' : 'Not Applicable'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Accidental Death Benefit</td>
                          <td className="border border-gray-300 p-3">
                            <span className="font-semibold text-green-600">Covered</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Policy Term</td>
                          <td className="border border-gray-300 p-3">{policy?.policy_term_years ? `${policy.policy_term_years} years` : 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Maturity Date</td>
                          <td className="border border-gray-300 p-3">{policy?.policy_maturity_date ? new Date(policy.policy_maturity_date).toLocaleDateString() : 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-3 font-semibold bg-gray-50">Maturity Value</td>
                          <td className="border border-gray-300 p-3">{policy?.maturity_value ? formatCurrency(policy.maturity_value) : 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Generated Date */}
                <div className="text-center text-sm text-gray-500 mt-8">
                  Generated on: {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowPdfModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGeneratePdf}
                data-generate-pdf
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Generate PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PolicyDetails
